/**
 * English copy. ADR-0005.
 *
 * Every user-facing string in the app lives here. Not for translation's sake in
 * v1.0 — English is the only locale — but because copy in this project is a
 * compliance artifact. Medical claims (L-08), exercise framing (L-18), privacy
 * claims and gender-neutral language (NFR-09) are all release gates, and having
 * every string in one reviewable file makes that review mechanical instead of a
 * hunt through components.
 *
 * Rules, enforced at review:
 *
 *  - Keys are semantic. Never English text used as its own key.
 *  - No sentence is assembled by concatenating fragments — word order is not
 *    universal. Use named placeholders.
 *  - Gender-neutral throughout: "people who menstruate", never "women";
 *    "your cycle", never "her cycle".
 *  - Never: "safe days", "infertile", "fertile window" without "estimated",
 *    "normal cycle", "irregular" as a judgement, or a condition name as a
 *    conclusion.
 */
export const en = {
  app: {
    // FR-115 / S-16: neutral. Nothing here says what the app is for.
    name: 'Thana',
    tagline: 'A private daily log.',
  },

  /** FR-111, S-02. The lock screen says nothing about what it is protecting. */
  lock: {
    title: 'Thana',
    prompt: 'Enter your PIN or passphrase',
    unlock: 'Unlock',
    unlocking: 'Unlocking…',
    // No distinction between a wrong PIN and any other failure: confirming the
    // PIN was merely wrong is a slightly better oracle for someone guessing.
    failed: 'That did not unlock. Try again.',
    // Said here as well as before the PIN is set, because this is where someone
    // who has forgotten it will be standing.
    noRecovery:
      'There is no way to recover a forgotten PIN. It is what encrypts your entries, so nobody — including us — can unlock them without it.',
    syncPaused: 'Multi-device sync is paused while the app is locked.',
  },

  /** FR-113, S-03. Nothing here hints at what it is covering. */
  neutral: {
    label: 'Notepad',
    placeholder: 'Notes',
    back: 'Thana',
    syncing: 'Syncing with cloud...',
    synced: 'All changes saved locally',
    sizeLabel: 'Font Size',
    words: 'words',
    chars: 'chars',
    readTime: 'min read',
    copied: 'Note copied to clipboard!',
    restored: 'Draft restored.',
    cleared: 'Draft cleared.',
    catPersonal: 'Personal Notes',
    catWork: 'Work Drafts',
    catIdeas: 'Ideas & Brainstorms',
    catLists: 'Lists & Checklists',
  },


  nav: {
    label: 'Sections',
    calendar: 'Calendar',
    today: 'Today',
    insights: 'Insights',
    data: 'Your data',
    settings: 'Settings',
    quickExit: 'Quick exit',
    quickExitTooltip: 'Quick exit: Hide data and show decoy notes screen',
    lockApp: 'Lock app',
    lockAppTooltip: 'Lock app immediately',
    setupPin: 'Set up PIN',
    setupPinTooltip: 'App is unprotected. Set up a PIN to lock and encrypt your data.',
  },

  calendar: {
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    logToday: 'Log today',
    weekday: {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
    },
    moreForToday: 'More for today',
    // Screen-reader label fragments. NFR-10: the calendar must be navigable and
    // comprehensible without sight, and colour never carries meaning alone, so
    // every visual marker has a word here.
    labelFlow: 'flow {flow}',
    labelLoggedEmpty: 'logged, nothing recorded',
    labelNote: 'has a note',
    labelSymptomCount: {
      one: '{count} symptom',
      other: '{count} symptoms',
    },
    // FR-304 / FR-406: spoken as an estimate. Never as a fact.
    labelPredicted: 'next period estimated to start',
    today: 'Today',
    empty: {
      title: 'Nothing logged yet',
      body: 'Tap a day to record how it went. Everything stays on this device.',
    },
  },

  entry: {
    title: 'Entry for {date}',
    flow: 'Flow',
    flowLevel: {
      none: 'None',
      spotting: 'Spotting',
      light: 'Light',
      medium: 'Medium',
      heavy: 'Heavy',
    },
    pain: 'Pain',
    painValue: '{value} out of 10',
    // 0 is a recorded value, not an absent one. See PainScale for why it needs
    // its own control.
    painNone: 'None',
    notRecorded: 'Not recorded',
    clearField: 'Clear',
    energy: 'Energy',
    energyLevel: {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
    },
    symptoms: 'Symptoms',
    note: 'Note',
    notePlaceholder: 'Anything you want to remember about today.',
    // FR-214. Says why a logged future period does not move the estimate.
    futureDate: 'This day is in the future. It is saved, but it will not affect estimates until it arrives.',
    nothingToRecord: 'Nothing to record',
    // FR-004: the difference between "logged nothing" and "never logged".
    nothingToRecordHint: 'Marks the day as checked, with nothing to note.',
    clear: 'Clear this day',
    close: 'Close',
  },

  /**
   * Built-in symptom labels. FR-204.
   *
   * Neutral and descriptive. Note "lowMood" rather than "depression": this app
   * does not name conditions, because naming one is a step toward a clinical
   * conclusion (L-08). The user is describing a day, not receiving a diagnosis.
   */
  symptom: {
    cramps: 'Cramps',
    headache: 'Headache',
    bloating: 'Bloating',
    breastTenderness: 'Breast tenderness',
    acne: 'Acne',
    nausea: 'Nausea',
    fatigue: 'Fatigue',
    backPain: 'Back pain',
    weakness: 'Weakness',
    anxiety: 'Anxiety',
    lowMood: 'Low mood',
    irritability: 'Irritability',
    stress: 'Stress',
    poorFocus: 'Trouble focusing',
    goodMood: 'Good mood',
    calm: 'Calm',
    focused: 'Focused',
    energetic: 'Energetic',
  },

  symptomCategory: {
    physical: 'Physical',
    mental: 'Mood and mind',
    energy: 'Energy',
    other: 'Other',
  },

  /** NFR-05: every failure state gets words, not a blank screen. */
  storage: {
    unavailable:
      'This browser will not let Thana store anything. Private or incognito windows often block local storage. Nothing you enter here would be saved.',
    loadFailed: 'Thana could not read your entries.',
    saveFailedQuota:
      'There is no room left to save. Export your data, then remove some old entries or free up space on your device.',
    saveFailedPremium:
      'You have reached the limit of 90 tracking days for the free trial. Enter a license key to log new entries.',
    saveFailed: 'That change could not be saved. Your entries are unchanged.',
    notPersistent:
      'This browser may delete Thana\'s data if your device runs low on space. Installing Thana to your home screen makes that much less likely.',
    possibleEviction:
      'Thana previously had entries on this device and now finds none. Your browser may have cleared them. If you have an export, you can import it.',
    dismiss: 'Dismiss',
    upgrade: 'Upgrade',
  },

  pwa: {
    installAndroid: 'Install Thana to your home screen for quick offline access and privacy.',
    actionAndroid: 'Install',
    installIOS: 'To install Thana: tap the Share button in Safari, then select "Add to Home Screen".',
  },


  /**
   * Estimate wording. FR-401..FR-406, L-08.
   *
   * Everything in this block is a regulatory boundary, not a style choice.
   * Changing any of it needs a copy review — see the forbidden-framing test in
   * src/copy/__tests__/forbiddenFraming.test.ts, which fails the build rather
   * than relying on someone remembering.
   *
   * Rules held here:
   *  - "estimated", never asserted.
   *  - Confidence given as a REASON, never a bare label and never a percentage:
   *    the model has no calibrated probability and implying one would be a
   *    quiet lie.
   *  - No contraception framing anywhere, in any direction.
   */
  prediction: {
    none: {
      insufficientHistory:
        'Not enough history to estimate yet. After two full cycles, an estimate will appear here.',
      suspended: 'Estimates are turned off. You can turn them back on at any time.',
    },
    estimate: 'Next period estimated {date}',
    /**
     * Plural forms, resolved through Intl.PluralRules (ADR-0005).
     *
     * Not cosmetic: "Based on 3 cycles that varied by about 1 days" reads as
     * carelessness, and carelessness in the one place the app talks about
     * uncertainty is the last place it can afford to look careless.
     */
    basis: {
      one: 'Based on your last {count} cycles, which varied by about a day.',
      other: 'Based on your last {count} cycles, which varied by about {variation} days.',
    },
    basisRough: {
      one: 'Based on {count} cycles that varied by about a day, so this could be a little out.',
      other:
        'Based on {count} cycles that varied by about {variation} days, so this could be several days out.',
    },
    basisExact:
      'Based on your last {count} cycles, which were all the same length.',
    cycleDay: 'Cycle day {day}',
    noCycleYet: 'Log a period to start tracking your cycle.',

    /**
     * FR-405. Shown only at medium or high confidence, and always with the
     * warning below it. Note the wording: "estimated fertile window", never
     * bare "fertile window"; "around", never a date presented as fact.
     */
    fertile: {
      heading: 'Estimated fertile window',
      range: '{from} to {to}',
      ovulation: 'Ovulation estimated around {date}',
      // The 14-day luteal phase is a population convention, not a measurement
      // of this person. Saying so is the difference between an estimate and a
      // claim.
      basis:
        'Worked out from your estimated next period using a typical 14-day gap, not from anything measured about you.',
      warning: 'Do not use this to prevent pregnancy.',
    },

    /** FR-412. Neutral, and the reason is never demanded. */
    suspend: {
      heading: 'Estimates',
      on: 'Estimates are on',
      off: 'Estimates are off',
      turnOff: 'Turn estimates off',
      turnOn: 'Turn estimates on',
      explain:
        'Useful if you are pregnant, recently gave birth, are going through menopause, are on continuous contraception, or simply do not want them. Logging is unaffected either way, and you do not have to say why.',
    },
  },

  /** FR-407, FR-411. */
  insights: {
    title: 'Insights',
    empty: 'Once you have logged a couple of cycles, your numbers will appear here.',
    typicalCycle: 'Typical cycle',
    shortest: 'Shortest',
    longest: 'Longest',
    // Two labels, because the value has two shapes. "Usually within: All the
    // same length" is the kind of sentence that only reads correctly to the
    // person who wrote the template.
    variation: 'Usually within',
    variationNone: 'Variation',
    typicalPeriod: 'Typical period',
    cyclesRecorded: 'Cycles recorded',
    daysLogged: 'Days logged',
    days: {
      one: '{count} day',
      other: '{count} days',
    },
    plusMinusDays: {
      one: '±{count} day',
      other: '±{count} days',
    },
    // A variation of zero is a real result, not a missing one — and "±0 days"
    // reads like a placeholder. Say what it means, with a label that fits it.
    noVariation: 'None so far',
    // "Typical", not "average" — these are medians, and a user comparing this
    // number against another app's or a clinician's deserves to know which.
    medianNote: 'These are middle values, not averages. One unusual month will not skew them.',
    /**
     * FR-411. Counted, shown, and never characterised. "Your cycles are
     * irregular" is a step toward a clinical conclusion (L-08) and it lands
     * badly on someone who already knows.
     */
    setAside: {
      heading: 'Set aside',
      body: 'These gaps were longer than 90 days or shorter than 15, so they are not counted in the numbers above. That usually means a stretch without logging.',
      item: {
        one: '{from} to {to}, {days} day',
        other: '{from} to {to}, {days} days',
      },
    },
  },

  disclaimer: {
    // L-09, FR-406. Persistent, on the same screen as any estimate.
    // This wording is a regulatory boundary. Changing it needs a copy review.
    short: 'Estimates, not measurements. Not medical advice.',
    full:
      'These are estimates based on what you logged, not measurements. ' +
      'Thana is not a medical device and does not give medical advice. ' +
      'Do not use it to prevent pregnancy. Talk to a healthcare professional ' +
      'about anything that concerns you.',
  },

  privacy: {
    // R-09: "open source" is proof to a fraction of a percent of the audience.
    // The rest need to see it. This line is a feature, not marketing.
    localOnly: 'Nothing has left this device.',
    localOnlyDetail:
      'Thana has no account and makes no network requests. Your entries are stored only in this browser.',
  },

  /** FR-601..607. Charter goals G3 and G4 live on this screen. */
  data: {
    protection: {
      heading: 'Protection',
      /**
       * The honest description of each state. With no PIN the key sits beside
       * the data, so calling that protection would be exactly the overclaim
       * this project exists to avoid (ADR-0006, FTC Section 5).
       */
      off: 'Your entries are stored scrambled, but the key is on this device next to them. Anyone who can read this browser\'s storage can read your entries. A PIN fixes that.',
      on: 'Your entries are encrypted with a key derived from your PIN. Without the PIN they cannot be read, including by us.',
      newPin: 'New PIN or passphrase',
      confirmPin: 'Enter it again',
      currentPin: 'Current PIN or passphrase',
      mismatch: 'Those did not match.',
      wrongPin: 'That did not match your current PIN.',
      set: 'Turn on protection',
      remove: 'Turn off protection',
      lockNow: 'Lock now',
      working: 'Working…',
      /**
       * Said BEFORE the PIN is set. A "forgot PIN" flow would mean the key was
       * recoverable without it, which would mean the encryption was decorative.
       */
      noRecovery:
        'There is no way to recover this. If you forget it, your entries are gone for good — so write it down somewhere safe, or export a backup first.',
      strength: {
        // Informational, never a gate. A green bar implying safety is the
        // pattern this avoids (T-10), so each line says what the limit is.
        weak: 'Short. Someone holding a copy of this device could work through every possibility.',
        fair: 'Better. Digits alone are still guessable given time and a copy of the database.',
        strong: 'A long passphrase is the strongest option here.',
      },
    },
    export: {
      heading: 'Export',
      body: 'Everything you have logged, in a documented format anyone can read. {count} days recorded.',
      passphrase: 'Passphrase for the encrypted file',
      passphraseHint:
        'This is separate from your app PIN on purpose, so a leaked backup does not weaken the app and changing one does not break the other.',
      encrypted: 'Export encrypted file',
      plainWarning:
        'A plain file is readable by anything that can open it, including anyone who finds it in your downloads or a cloud backup.',
      plain: 'Export plain file anyway',
    },
    import: {
      heading: 'Import',
      choose: 'Choose an export file',
      passphrase: 'Passphrase for this file',
      unlockFile: 'Unlock file',
      wrongPassphrase: 'That passphrase did not open the file.',
      unreadable: 'That file is not a Thana export.',
      preview: '{added} days to add, {changed} to change, {same} already the same.',
      merge: 'Merge',
      replace: 'Replace',
      modeHint:
        'Merge keeps whichever version of a day is newer. Replace takes the file as correct. Neither touches days the file does not mention.',
    },
    delete: {
      heading: 'Delete everything',
      body: 'Removes all {count} days you have logged from this device, and destroys the key that could read them.',
      /**
       * The honest limit (T-12, legal map Section 3.3). Promising more than this
       * would be the easiest lie in the app to tell, and the one most likely to
       * matter to someone.
       */
      honest:
        'This cannot be undone. Your entries are removed from this device immediately and the key that could decrypt them is destroyed. If you ever used sync, encrypted copies may still exist elsewhere — they become permanently unreadable, but we cannot promise the files themselves are gone.',
      typeToConfirm: 'Type {phrase} to confirm',
      confirm: 'Delete everything',
      done: 'Deleted.',
    },
  },

  /**
   * Sync. FR-501..FR-509, L-04.
   *
   * This copy IS the legal basis. GDPR Article 9(2)(a) and KVKK *açık rıza*
   * require consent that is specific, informed and unambiguous — which means the
   * user has to be able to understand, from this screen alone, what leaves the
   * device, who receives it, what they can see, and what cannot be undone.
   *
   * Every claim here is checked against what the code does. "They cannot read it"
   * is true because of sealed envelopes; "they can see when you sync" is true
   * because of traffic timing (T-08). Both are said, because saying only the first
   * would be the overclaim the FTC pursues.
   */
  sync: {
    heading: 'Sync between devices',
    off: 'Sync is off. Nothing leaves this device.',
    on: 'Sync is on.',
    lockHint: 'Sync only runs while the app is open and unlocked. It pauses automatically when the app locks to protect your keys.',

    /** The consent screen. Each line answers a question a regulator would ask. */
    consent: {
      tabRegister: 'Create sync account',
      tabLogin: 'Connect existing device',
      title: 'Before you turn on sync',
      connectTitle: 'Connect an existing device',
      whatLeaves:
        'Your entries are encrypted on this device before they are sent. The key stays with you, so nobody who stores or carries them can read what you logged — not the relay operators, and not us.',
      whatTheySee:
        'What a relay can see is that a device at your IP address sent something, how big it was, and when. It cannot see what any of it says. If that matters to you, a VPN or Tor changes what they see.',
      whoReceives: 'Your encrypted entries would be stored by:',
      relayLine: '{operator} — {jurisdiction}',
      relayContact: 'Contact: {contact}',
      jurisdictionNote:
        'Jurisdiction matters because it decides whose courts can compel a relay operator. They would only be able to hand over data they cannot read, and your IP address.',
      passphrase:
        'You will need a sync passphrase. It is what encrypts your entries for other devices, so it cannot be recovered and it cannot be reset. Write it down somewhere safe.',
      noUndo:
        'Once an encrypted copy has reached a relay, we cannot promise it is ever deleted. Deleting your data destroys the key, which makes those copies permanently unreadable — that is the strongest promise this design can honestly make.',
      withdraw: 'You can turn sync off at any time, in one tap. That stops all network activity.',
      agree: 'I understand. Turn on sync',
      connect: 'I understand. Connect device',
      cancel: 'Not now',
    },

    /** FR-504. A weak passphrase on a world-readable graph is crackable offline. */
    /**
     * The username is not a secret and is never sent anywhere. It salts the key
     * derivation so that two people who choose the same passphrase do not end up
     * with the same account, and so an attacker must attack each account
     * separately rather than once for everyone.
     *
     * The copy says "exactly" because it is folded for case and outer spacing but
     * nothing else — "my phone" and "myphone" are different accounts — and a
     * mismatch on a second device presents as sync simply not happening, with
     * nothing on screen to explain it.
     */
    username: {
      label: 'Sync username',
      hint: 'Anything you will remember. You will type this and your passphrase on your other device, exactly the same way.',
      required: 'Choose a sync username.',
      loginLabel: 'Your sync username',
      loginHint: 'The username you chose on your first device.',
    },

    passphrase: {
      label: 'Sync passphrase',
      confirm: 'Enter it again',
      mismatch: 'Those did not match.',
      tooWeak:
        'Too short to protect entries that leave the device. Anyone who copies them from a relay could work through the possibilities offline, and unlike your PIN there is no device in the way. Use several words.',
      hint: 'Several unrelated words are far stronger than one word with numbers.',
      loginLabel: 'Your sync passphrase',
    },

    /** FR-507. Never claim "synced" for a write nobody has acknowledged. */
    status: {
      idle: 'Up to date',
      pending: 'Saving {count} change(s)…',
      offline: 'Offline. Changes are saved here and will sync when you reconnect.',
      noRelays:
        'No relay is available. Your entries are safe on this device; nothing has synced.',
      listRejected:
        'The relay list could not be verified, so it was ignored. Sync is using the last list that was verified.',
      relaysChanged:
        'A new relay has been added to the list. Sync is paused until you agree to it, because it would be a new place your encrypted entries are stored.',
      reviewRelays: 'Review and continue',
      consentOutdated:
        'How sync works has changed since you agreed to it. Sync is paused until you read the new description.',
    },

    /**
     * Deriving the identity can fail for one realistic reason: the app locked
     * while the form was open, so there is no data key to store it under.
     *
     * The message does not name that cause, because the recovery is the same
     * whatever went wrong and a precise diagnosis here would be guesswork
     * presented as fact. What it does promise is the part that matters: nothing
     * was turned on, so the user is not left wondering whether some of their
     * history left the device.
     */
    setupFailed: 'Sync could not be set up. Nothing was turned on and nothing has left this device. Try again.',

    turnOff: 'Turn off sync',
    turnOn: 'Turn on sync',
  },

  /**
   * A waiting service worker, in words.
   *
   * Deliberately a prompt rather than a silent reload. `registerType` is
   * `prompt` for a reason: activating a new worker reloads the page, and this
   * app has screens someone may be part-way through — a note being typed, a
   * passphrase being entered. Losing that to a background update is worse than
   * running an old version for another minute.
   *
   * The wording says what changes and what it costs, and does not claim the
   * update is important, because most are not and crying wolf trains people to
   * ignore the one that is.
   */
  update: {
    available: 'A new version is ready. It will be used the next time you reload.',
    action: 'Reload now',
  },

  common: {
    save: 'Save',
    cancel: 'Cancel',
    loading: 'Loading',
    error: 'Something went wrong',
    retry: 'Try again',
  },
} as const

export type Copy = typeof en
